import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { BladeAction, FencerColorId, FencerState } from './types';
import { FENCER_COLORS } from './types';
import type { CharacterId, CharacterPreset } from './characters';
import { CHARACTERS, getPreferredModelUrl } from './characters';
import { createCombatClips, retargetClipToSkeleton } from './CombatClips';
import { makeBladeMaterial, makeGripMaterial, makeGuardMaterial } from './materials';
import { animDebug, type AnimDebugSnapshot } from './AnimDebug';

/** Reused for additive pose after the mixer (avoids GC). */
const _eul = new THREE.Euler();
const _qAdd = new THREE.Quaternion();

/**
 * Mixer writes quaternions; Object3D.rotation eulers are often stale.
 * Adding to .rotation.x without syncing overwrites the mixer pose with garbage.
 * Always compose additive local euler onto the current quaternion.
 */
function addLocalEuler(obj: THREE.Object3D, dx: number, dy = 0, dz = 0): void {
  if (dx === 0 && dy === 0 && dz === 0) return;
  _eul.set(dx, dy, dz, obj.rotation.order);
  _qAdd.setFromEuler(_eul);
  obj.quaternion.multiply(_qAdd);
}

/** Drop scale tracks — Blender often exports identity STEP scales that still cost blend noise. */
function sanitizeClip(clip: THREE.AnimationClip): THREE.AnimationClip {
  const tracks = clip.tracks.filter((t) => !t.name.endsWith('.scale'));
  if (tracks.length === clip.tracks.length) return clip;
  return new THREE.AnimationClip(clip.name, clip.duration, tracks);
}

const PISTE_HALF = 7;

type BoneMap = {
  hips?: THREE.Object3D;
  spine?: THREE.Object3D;
  spine1?: THREE.Object3D;
  spine2?: THREE.Object3D;
  neck?: THREE.Object3D;
  head?: THREE.Object3D;
  leftArm?: THREE.Object3D;
  leftForeArm?: THREE.Object3D;
  leftHand?: THREE.Object3D;
  rightShoulder?: THREE.Object3D;
  rightArm?: THREE.Object3D;
  rightForeArm?: THREE.Object3D;
  rightHand?: THREE.Object3D;
  leftUpLeg?: THREE.Object3D;
  leftLeg?: THREE.Object3D;
  rightUpLeg?: THREE.Object3D;
  rightLeg?: THREE.Object3D;
};

type GltfCache = { scene: THREE.Group; animations: THREE.AnimationClip[] };
const gltfCache = new Map<string, GltfCache>();

async function loadGltfUrl(url: string): Promise<GltfCache> {
  const hit = gltfCache.get(url);
  if (hit) return hit;
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(url);
  const entry = { scene: gltf.scene, animations: gltf.animations };
  gltfCache.set(url, entry);
  return entry;
}

async function loadCharacterGltf(preset: CharacterPreset): Promise<{ data: GltfCache; url: string }> {
  const preferred = getPreferredModelUrl(preset);
  try {
    const data = await loadGltfUrl(preferred);
    return { data, url: preferred };
  } catch {
    try {
      if (preferred !== preset.preferredUrl) {
        const data = await loadGltfUrl(preset.preferredUrl);
        return { data, url: preset.preferredUrl };
      }
    } catch {
      /* fall through */
    }
    const data = await loadGltfUrl(preset.fallbackUrl);
    return { data, url: preset.fallbackUrl };
  }
}

function findBone(root: THREE.Object3D, ...names: string[]): THREE.Object3D | undefined {
  let found: THREE.Object3D | undefined;
  root.traverse((o) => {
    if (found) return;
    const n = o.name.replace(/^mixamorig[:_]?/i, '');
    if (names.some((x) => n === x || o.name.endsWith(x) || n.toLowerCase() === x.toLowerCase())) {
      found = o;
    }
  });
  return found;
}

function pickClip(anims: THREE.AnimationClip[], candidates: string[]): THREE.AnimationClip | undefined {
  for (const c of candidates) {
    const hit = anims.find((a) => a.name.toLowerCase() === c.toLowerCase());
    if (hit) return hit;
  }
  for (const c of candidates) {
    const hit = anims.find((a) => a.name.toLowerCase().includes(c.toLowerCase()));
    if (hit) return hit;
  }
  return anims[0];
}

type CombatActionName =
  | 'Combat_Lunge'
  | 'Combat_Attack'
  | 'Combat_ParryHigh'
  | 'Combat_ParryLow'
  | 'Combat_Fleche'
  | 'Combat_EnGarde';

/**
 * Skinned fencer using the rigged 3dUVpro mesh (fencer-rigged.glb):
 * Idle/Walk/Run + Lunge/Attack/Parry/Fleche, sword on RightHand, mouse aim.
 * Falls back to Soldier + procedural CombatClips if needed.
 */
export class Fencer {
  readonly root = new THREE.Group();
  readonly state: FencerState;
  readonly tipWorld = new THREE.Vector3();
  readonly lameCenter = new THREE.Vector3();
  readonly chestBox = new THREE.Box3();
  readonly characterId: CharacterId;

  private preset: CharacterPreset;
  private modelRoot = new THREE.Group();
  private mixer: THREE.AnimationMixer | null = null;
  private loco: Partial<Record<'idle' | 'walk' | 'run', THREE.AnimationAction>> = {};
  private combat: Partial<Record<CombatActionName, THREE.AnimationAction>> = {};
  private activeCombat: THREE.AnimationAction | null = null;
  private bones: BoneMap = {};
  private bladeTip!: THREE.Object3D;
  private weapon = new THREE.Group();
  private weaponSocket = new THREE.Group();
  private lameProxy = new THREE.Object3D();
  private priorityRing!: THREE.Mesh;
  private tipMat!: THREE.MeshStandardMaterial;
  private tipGlowMat!: THREE.MeshBasicMaterial;
  private accentMats: THREE.MeshStandardMaterial[] = [];
  private accentBasic: THREE.MeshBasicMaterial[] = [];
  private meshMats: THREE.MeshStandardMaterial[] = [];
  private lastFoot = 0;
  private breath = 0;
  private ready = false;
  private modelScale = 1;
  private skinned = false;
  /** True when GLB shipped its own Lunge/Attack/etc. — skip Soldier-style EnGarde layer. */
  private hasEmbeddedCombat = false;
  private loadedUrl = '';
  private debugAcc = 0;
  private prevLoco: 'idle' | 'walk' | 'run' | '' = '';
  footstepPulse = false;
  justStartedAttack: BladeAction | null = null;

  private constructor(isPlayer: boolean, colorId: FencerColorId, characterId: CharacterId) {
    this.characterId = characterId;
    this.preset = CHARACTERS[characterId];
    this.state = {
      x: isPlayer ? -2.2 : 2.2,
      facing: isPlayer ? 1 : -1,
      stance: 0,
      bladeAngle: 0,
      action: 'idle',
      actionT: 0,
      actionDur: 0.35,
      invuln: 0,
      energy: 1,
      hasPriority: false,
      canRiposte: false,
      isPlayer,
      colorId,
    };
    this.root.add(this.modelRoot);
    const def = FENCER_COLORS[colorId];
    const ringMat = new THREE.MeshBasicMaterial({
      color: def.accent,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.accentBasic.push(ringMat);
    this.priorityRing = new THREE.Mesh(new THREE.RingGeometry(0.32, 0.4, 32), ringMat);
    this.priorityRing.rotation.x = -Math.PI / 2;
    this.priorityRing.position.y = 0.02;
    this.root.add(this.priorityRing);
  }

  static async create(
    isPlayer: boolean,
    colorId: FencerColorId,
    characterId: CharacterId = 'fencer',
  ): Promise<Fencer> {
    const f = new Fencer(isPlayer, colorId, characterId);
    await f.loadModel();
    f.setColor(colorId);
    f.syncTransform();
    return f;
  }

  get isReady(): boolean {
    return this.ready;
  }

  get label(): string {
    return this.preset.label;
  }

  private async loadModel(): Promise<void> {
    const { data: gltf, url } = await loadCharacterGltf(this.preset);
    this.loadedUrl = url;
    // SkeletonUtils keeps skin bindings intact for multiple instances
    const scene = SkeletonUtils.clone(gltf.scene) as THREE.Group;
    scene.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) {
        const mesh = o as THREE.Mesh;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        const cloned = mats.map((m) => {
          const c = (m as THREE.Material).clone() as THREE.MeshStandardMaterial;
          if ('metalness' in c) {
            c.metalness = Math.min(0.5, (c.metalness ?? 0.2) + 0.1);
            c.roughness = Math.max(0.4, (c.roughness ?? 0.7) * 0.92);
            this.meshMats.push(c);
          }
          return c;
        });
        mesh.material = cloned.length === 1 ? cloned[0] : cloned;
      }
    });

    const box = new THREE.Box3().setFromObject(scene);
    const size = box.getSize(new THREE.Vector3());
    this.modelScale = size.y > 0.01 ? this.preset.targetHeight / size.y : 1;
    scene.scale.setScalar(this.modelScale);
    box.setFromObject(scene);
    scene.position.y = -box.min.y;
    scene.rotation.y = this.preset.modelYaw;
    this.modelRoot.add(scene);

    this.bones = {
      hips: findBone(scene, 'Hips'),
      spine: findBone(scene, 'Spine'),
      spine1: findBone(scene, 'Spine1'),
      spine2: findBone(scene, 'Spine2'),
      neck: findBone(scene, 'Neck'),
      head: findBone(scene, 'Head'),
      leftArm: findBone(scene, 'LeftArm'),
      leftForeArm: findBone(scene, 'LeftForeArm'),
      leftHand: findBone(scene, 'LeftHand'),
      rightShoulder: findBone(scene, 'RightShoulder'),
      rightArm: findBone(scene, 'RightArm'),
      rightForeArm: findBone(scene, 'RightForeArm'),
      rightHand: findBone(scene, 'RightHand'),
      leftUpLeg: findBone(scene, 'LeftUpLeg'),
      leftLeg: findBone(scene, 'LeftLeg'),
      rightUpLeg: findBone(scene, 'RightUpLeg'),
      rightLeg: findBone(scene, 'RightLeg'),
    };
    this.skinned = !!(this.bones.rightHand || this.bones.rightArm || this.bones.hips);

    // —— Sword socket on right hand ——
    this.weapon = this.makeFoil(FENCER_COLORS[this.state.colorId]);
    this.weaponSocket = new THREE.Group();
    this.weaponSocket.name = 'WeaponSocket';
    // Mixamo right hand: blade along -Y of hand, grip in palm
    this.weaponSocket.rotation.set(Math.PI * 0.5, 0, Math.PI * 0.5);
    this.weaponSocket.position.set(0, 0.06, 0.02);
    this.weapon.scale.setScalar(1 / Math.max(0.001, this.modelScale));
    this.weaponSocket.add(this.weapon);

    const hand = this.bones.rightHand;
    if (hand) {
      hand.add(this.weaponSocket);
    } else {
      this.weaponSocket.position.set(0.25, 1.15, 0.2);
      this.modelRoot.add(this.weaponSocket);
    }
    this.bladeTip = this.weapon.getObjectByName('tip')!;

    this.lameProxy.position.set(0, 1.25, 0);
    this.modelRoot.add(this.lameProxy);

    // —— Animation mixer ——
    this.mixer = new THREE.AnimationMixer(scene);

    const animNames = gltf.animations.map((a) => a.name);
    const idleClip = pickClip(gltf.animations, this.preset.animIdle);
    const walkClip = pickClip(gltf.animations, this.preset.animWalk);
    const runClip = pickClip(gltf.animations, this.preset.animRun) ?? walkClip;

    const bindLoco = (clip: THREE.AnimationClip | undefined, key: 'idle' | 'walk' | 'run', w: number) => {
      if (!clip) return;
      const clean = sanitizeClip(clip);
      const action = this.mixer!.clipAction(clean);
      action.play();
      action.setEffectiveWeight(w);
      action.enabled = true;
      // Normalize time so exclusive crossfades stay in sync-ish
      action.setEffectiveTimeScale(1);
      this.loco[key] = action;
    };
    bindLoco(idleClip, 'idle', 1);
    bindLoco(walkClip, 'walk', 0);
    bindLoco(runClip, 'run', 0);

    // Combat kit: prefer clips baked into the rigged fencer GLB, else procedural
    const combatFromGltf: { key: CombatActionName; names: string[] | undefined }[] = [
      { key: 'Combat_Lunge', names: this.preset.animLunge },
      { key: 'Combat_Attack', names: this.preset.animAttack },
      { key: 'Combat_ParryHigh', names: this.preset.animParryHigh },
      { key: 'Combat_ParryLow', names: this.preset.animParryLow },
      { key: 'Combat_Fleche', names: this.preset.animFleche },
      { key: 'Combat_EnGarde', names: this.preset.animEnGarde },
    ];
    let embedded = 0;
    for (const { key, names } of combatFromGltf) {
      if (!names) continue;
      const clip = pickClip(gltf.animations, names);
      if (!clip) continue;
      const renamed = sanitizeClip(clip.clone());
      renamed.name = key;
      const action = this.mixer.clipAction(renamed);
      if (key === 'Combat_EnGarde') {
        action.setLoop(THREE.LoopRepeat, Infinity);
      } else {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      action.enabled = true;
      action.setEffectiveWeight(0);
      action.blendMode = THREE.NormalAnimationBlendMode;
      this.combat[key] = action;
      if (key !== 'Combat_EnGarde') embedded++;
    }
    this.hasEmbeddedCombat = embedded >= 3;

    // Procedural Mixamo clips fill gaps only (never on top of embedded combat)
    for (const raw of createCombatClips()) {
      const name = raw.name as CombatActionName;
      if (this.combat[name]) continue;
      if (this.hasEmbeddedCombat) {
        animDebug.note(`skip procedural ${name} (embedded Mixamo combat present)`);
        continue;
      }
      const clip = retargetClipToSkeleton(raw, scene);
      const action = this.mixer.clipAction(sanitizeClip(clip));
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
      action.enabled = true;
      action.setEffectiveWeight(0);
      this.combat[name] = action;
    }

    // Soft en garde only as a light layer when idle — never stacked during combat.
    // With Mixamo-retargeted mesh, EnGarde is optional and kept subtle.
    const enGarde = this.combat.Combat_EnGarde;
    if (enGarde) {
      enGarde.setLoop(THREE.LoopRepeat, Infinity);
      enGarde.play();
      enGarde.setEffectiveWeight(this.hasEmbeddedCombat ? 0 : 0.22);
      enGarde.blendMode = THREE.NormalAnimationBlendMode;
      animDebug.note(
        this.hasEmbeddedCombat
          ? 'EnGarde available (weight 0 until idle blend wants it lightly)'
          : 'EnGarde layer enabled (fallback)',
      );
    }

    this.mixer.addEventListener('finished', (e) => {
      const act = e.action as THREE.AnimationAction;
      if (act === this.activeCombat) {
        animDebug.anim(`combat finished ${act.getClip().name}`);
        this.fadeCombatOut(0.2);
        if (
          this.state.action === 'lunge' ||
          this.state.action === 'fleche' ||
          this.state.action === 'riposte'
        ) {
          this.state.action = 'recover';
          this.state.actionT = 0;
          this.state.actionDur = 0.28;
        } else if (this.state.action === 'parryHigh' || this.state.action === 'parryLow') {
          this.state.action = 'idle';
          this.state.actionT = 0;
        }
      }
    });

    // Bone inventory for debug
    const boneNames: string[] = [];
    scene.traverse((o) => {
      if ((o as THREE.Bone).isBone) boneNames.push(o.name);
    });
    const who = this.state.isPlayer ? 'P1' : 'P2';
    animDebug.info(
      `${who} loaded ${url} scale=${this.modelScale.toFixed(3)} skinned=${this.skinned} bones=${boneNames.length}`,
    );
    animDebug.info(
      `${who} gltf anims=[${animNames.join(', ')}] idle=${idleClip?.name ?? '-'} walk=${walkClip?.name ?? '-'} run=${runClip?.name ?? '-'} embeddedCombat=${embedded}`,
    );
    animDebug.info(
      `${who} hand=${this.bones.rightHand?.name ?? 'none'} arm=${this.bones.rightArm?.name ?? 'none'} foundBones=${Object.entries(
        this.bones,
      )
        .filter(([, v]) => !!v)
        .map(([k]) => k)
        .join(',')}`,
    );
    if (idleClip) {
      const sample = idleClip.tracks.slice(0, 6).map((t) => t.name);
      animDebug.info(`${who} Idle tracks(${idleClip.tracks.length}): ${sample.join(' | ')}…`);
    }

    this.ready = true;
  }

  private makeFoil(def: (typeof FENCER_COLORS)[FencerColorId]): THREE.Group {
    const g = new THREE.Group();
    const bladeMat = makeBladeMaterial();
    const guardMat = makeGuardMaterial();
    const gripMat = makeGripMaterial();

    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.95, 0.006), bladeMat);
    blade.position.y = -0.58;
    blade.castShadow = true;
    g.add(blade);

    this.tipMat = new THREE.MeshStandardMaterial({
      color: 0xf0f4ff,
      metalness: 0.95,
      roughness: 0.15,
      emissive: def.glow,
      emissiveIntensity: 0.45,
    });
    const tip = new THREE.Mesh(new THREE.SphereGeometry(0.017, 10, 10), this.tipMat);
    tip.position.y = -1.06;
    tip.name = 'tip';
    g.add(tip);

    this.tipGlowMat = new THREE.MeshBasicMaterial({
      color: def.accent,
      transparent: true,
      opacity: 0.28,
      depthWrite: false,
    });
    this.accentBasic.push(this.tipGlowMat);
    const glow = new THREE.Mesh(new THREE.SphereGeometry(0.048, 8, 8), this.tipGlowMat);
    glow.position.y = -1.06;
    g.add(glow);

    const guard = new THREE.Mesh(
      new THREE.SphereGeometry(0.078, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.55),
      guardMat,
    );
    guard.position.y = -0.1;
    guard.rotation.x = Math.PI;
    guard.scale.set(1, 0.55, 1);
    g.add(guard);

    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.13, 0.05), gripMat);
    grip.position.set(0.02, 0.02, 0);
    grip.rotation.z = 0.35;
    g.add(grip);

    const stripeMat = new THREE.MeshStandardMaterial({
      color: def.accent,
      metalness: 0.55,
      roughness: 0.35,
      emissive: def.accent,
      emissiveIntensity: 0.3,
    });
    this.accentMats.push(stripeMat);
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.11, 0.052), stripeMat);
    stripe.position.set(0.02, 0.02, 0);
    stripe.rotation.z = 0.35;
    g.add(stripe);

    return g;
  }

  setColor(colorId: FencerColorId): void {
    this.state.colorId = colorId;
    const def = FENCER_COLORS[colorId];
    for (const m of this.meshMats) {
      if (!m.userData.baseColor) m.userData.baseColor = m.color.clone();
      const b = m.userData.baseColor as THREE.Color;
      m.color.copy(b).lerp(new THREE.Color(def.lame), 0.4);
      if (m.emissive) {
        m.emissive.setHex(def.accent);
        m.emissiveIntensity = 0.05;
      }
    }
    for (const m of this.accentMats) {
      m.color.setHex(def.accent);
      m.emissive.setHex(def.accent);
    }
    for (const m of this.accentBasic) m.color.setHex(def.accent);
    if (this.tipMat) this.tipMat.emissive.setHex(def.glow);
  }

  startAction(action: BladeAction, duration = 0.35): boolean {
    if (this.state.action === 'lunge' || this.state.action === 'fleche') {
      if (action !== 'recover' && action !== 'idle') return false;
    }
    if (this.state.action === 'parryHigh' || this.state.action === 'parryLow') {
      if (action === 'parryHigh' || action === 'parryLow') return false;
    }
    this.state.action = action;
    this.state.actionT = 0;
    this.state.actionDur = duration;
    this.justStartedAttack = action;
    if (action === 'lunge' || action === 'fleche' || action === 'riposte') {
      this.state.hasPriority = true;
      this.state.canRiposte = false;
    }
    if (action === 'parryHigh' || action === 'parryLow') {
      this.state.canRiposte = false;
    }

    // Play skeletal combat clip
    const map: Partial<Record<BladeAction, CombatActionName>> = {
      lunge: 'Combat_Lunge',
      fleche: 'Combat_Fleche',
      riposte: 'Combat_Attack',
      parryHigh: 'Combat_ParryHigh',
      parryLow: 'Combat_ParryLow',
    };
    // Space/click attack uses Attack clip for snappier feel; lunge for full commit
    if (action === 'lunge' && duration < 0.48) {
      this.playCombat('Combat_Attack', duration);
    } else {
      const name = map[action];
      if (name) this.playCombat(name, duration);
    }
    return true;
  }

  /** Explicit mouse LMB attack (short thrust). */
  playAttack(): boolean {
    if (this.state.energy < 0.15) return false;
    if (this.state.action === 'lunge' || this.state.action === 'fleche') return false;
    this.state.energy -= 0.18;
    return this.startAction('lunge', 0.42);
  }

  /** Explicit mouse RMB parry — high if aim up, low if aim down. */
  playParryFromAim(): boolean {
    const high = this.state.stance >= 0;
    return this.startAction(high ? 'parryHigh' : 'parryLow', 0.36);
  }

  private playCombat(name: CombatActionName, duration: number): void {
    const next = this.combat[name];
    if (!next || !this.mixer) {
      animDebug.warn(`playCombat missing clip ${name}`);
      return;
    }
    if (this.activeCombat && this.activeCombat !== next) {
      this.activeCombat.fadeOut(0.08);
      this.activeCombat.setEffectiveWeight(0);
    }
    next.reset();
    next.enabled = true;
    next.setEffectiveWeight(1);
    // Scale clip time to requested duration
    const clipDur = next.getClip().duration || 0.4;
    next.setEffectiveTimeScale(clipDur / Math.max(0.15, duration));
    next.fadeIn(0.04);
    next.play();
    this.activeCombat = next;
    animDebug.anim(
      `${this.state.isPlayer ? 'P1' : 'P2'} play ${name} dur=${duration.toFixed(2)} clip=${clipDur.toFixed(2)}s tracks=${next.getClip().tracks.length}`,
    );

    // CRITICAL: zero ALL locomotion while combat owns the skeleton.
    // Snapshot showed idle@0.74 + Attack@1 → wild arm slerp.
    for (const k of ['idle', 'walk', 'run'] as const) {
      const a = this.loco[k];
      if (a) a.setEffectiveWeight(0);
    }
    const eg = this.combat.Combat_EnGarde;
    if (eg) eg.setEffectiveWeight(0);
  }

  private fadeCombatOut(fade = 0.2): void {
    if (this.activeCombat) {
      this.activeCombat.fadeOut(fade);
      this.activeCombat = null;
    }
    // Restore idle under the fade so we do not freeze on last combat frame
    if (this.loco.idle) {
      this.loco.idle.setEffectiveWeight(1);
      this.loco.idle.play();
    }
    if (this.loco.walk) this.loco.walk.setEffectiveWeight(0);
    if (this.loco.run) this.loco.run.setEffectiveWeight(0);
  }

  get isAttacking(): boolean {
    return (
      this.state.action === 'lunge' ||
      this.state.action === 'fleche' ||
      this.state.action === 'riposte'
    );
  }

  get isParrying(): boolean {
    return this.state.action === 'parryHigh' || this.state.action === 'parryLow';
  }

  consumeJustStarted(): BladeAction | null {
    const a = this.justStartedAttack;
    this.justStartedAttack = null;
    return a;
  }

  update(
    dt: number,
    inputMove = 0,
    wantLunge = false,
    wantFleche = false,
    parry: 0 | 1 | -1 = 0,
    wantRiposte = false,
  ): void {
    if (!this.ready) {
      this.syncTransform();
      return;
    }

    const s = this.state;
    s.invuln = Math.max(0, s.invuln - dt);
    s.energy = Math.min(1, s.energy + dt * 0.15);
    this.breath += dt;

    const worldDir = Math.abs(inputMove) > 0.1 ? Math.sign(inputMove) : 0;
    const isAdvancing = worldDir !== 0 && worldDir === s.facing;
    const isRetreating = worldDir !== 0 && worldDir === -s.facing;

    if (s.action !== 'idle' && s.action !== 'advance' && s.action !== 'retreat') {
      s.actionT += dt;
      if (s.actionT >= s.actionDur) {
        if (s.action === 'lunge' || s.action === 'fleche' || s.action === 'riposte') {
          s.action = 'recover';
          s.actionT = 0;
          s.actionDur = 0.28;
        } else if (s.action === 'recover') {
          s.action = 'idle';
          s.actionT = 0;
          this.fadeCombatOut(0.15);
        } else {
          s.action = 'idle';
          s.actionT = 0;
        }
      }
    }

    if (s.action === 'idle' || s.action === 'advance' || s.action === 'retreat') {
      if (wantRiposte && s.canRiposte) {
        this.startAction('riposte', 0.36);
      } else if (wantFleche && s.energy > 0.35) {
        s.energy -= 0.35;
        this.startAction('fleche', 0.48);
      } else if (wantLunge && s.energy > 0.18) {
        s.energy -= 0.18;
        this.startAction('lunge', 0.52);
      } else if (parry === 1) {
        this.startAction('parryHigh', 0.36);
      } else if (parry === -1) {
        this.startAction('parryLow', 0.36);
      } else if (isAdvancing) {
        s.action = 'advance';
      } else if (isRetreating) {
        s.action = 'retreat';
      } else {
        s.action = 'idle';
      }
    }

    // World movement
    let speed = 0;
    const t = s.actionDur > 0 ? s.actionT / s.actionDur : 0;
    const fast = Math.abs(inputMove) > 1.1 ? 1.25 : 1;
    if ((s.action === 'advance' || s.action === 'retreat') && worldDir !== 0) {
      speed = (s.action === 'advance' ? 1.55 : 1.7) * worldDir * fast;
    }
    if (s.action === 'lunge') {
      speed = Math.sin(Math.min(1, t) * Math.PI) * 4.5 * s.facing;
    }
    if (s.action === 'fleche') {
      speed = 5.8 * s.facing * (t < 0.7 ? 1 : 0.25);
    }
    if (s.action === 'riposte') {
      speed = 2.5 * s.facing * Math.sin(Math.min(1, t) * Math.PI);
    }
    if (s.action === 'recover') {
      speed = -1.25 * s.facing * (1 - t);
    }

    const prevX = s.x;
    s.x += speed * dt;
    s.x = THREE.MathUtils.clamp(s.x, -PISTE_HALF, PISTE_HALF);

    this.footstepPulse = false;
    if ((s.action === 'advance' || s.action === 'retreat') && Math.abs(s.x - prevX) > 0.001) {
      this.lastFoot += dt;
      if (this.lastFoot > 0.3) {
        this.lastFoot = 0;
        this.footstepPulse = true;
      }
    }

    const moving = s.action === 'advance' || s.action === 'retreat';
    const sprint = s.action === 'fleche' || (moving && Math.abs(inputMove) > 1.1);
    this.blendLocomotion(dt, moving || s.action === 'lunge', sprint);

    this.mixer?.update(dt);
    // Additive layers AFTER mixer — must compose on quaternion, not stale euler
    this.applyCombatPose();
    this.applyMouseAimToArm();

    if (this.tipMat) {
      const hot = this.isAttacking || s.hasPriority ? 1.35 : 0.4;
      this.tipMat.emissiveIntensity = THREE.MathUtils.lerp(this.tipMat.emissiveIntensity, hot, 0.15);
    }
    const ringMat = this.priorityRing.material as THREE.MeshBasicMaterial;
    ringMat.opacity = THREE.MathUtils.lerp(
      ringMat.opacity,
      s.hasPriority || s.canRiposte ? 0.8 : 0,
      0.12,
    );
    this.priorityRing.rotation.z += dt * 1.5;

    this.syncTransform();
    this.updateTip();

    if (this.state.isPlayer) {
      this.debugAcc += dt;
      if (animDebug.isEnabled && this.debugAcc >= 0.2) {
        this.debugAcc = 0;
        animDebug.pushSnapshot(this.captureDebugSnapshot());
      } else if (!animDebug.isEnabled) {
        this.debugAcc = 0;
      }
    }
  }

  private blendLocomotion(dt: number, moving: boolean, sprint: boolean): void {
    // Exclusive weights only — never stack Idle+Walk (absolute quat slerp).
    const target: 'idle' | 'walk' | 'run' = !moving ? 'idle' : sprint ? 'run' : 'walk';
    if (target !== this.prevLoco) {
      if (this.state.isPlayer && this.prevLoco) {
        animDebug.anim(`loco ${this.prevLoco} → ${target}`);
      }
      this.prevLoco = target;
    }
    const combatBusy =
      !!this.activeCombat &&
      this.activeCombat.isRunning() &&
      this.activeCombat.getEffectiveWeight() > 0.05;

    if (combatBusy) {
      // Hard exclusive: combat clip alone drives bones
      for (const k of ['idle', 'walk', 'run'] as const) {
        const a = this.loco[k];
        if (a) a.setEffectiveWeight(0);
      }
      const eg = this.combat.Combat_EnGarde;
      if (eg) eg.setEffectiveWeight(0);
      return;
    }

    const fade = 1 - Math.pow(0.0004, dt); // snappier crossfade
    const setW = (name: 'idle' | 'walk' | 'run', w: number) => {
      const a = this.loco[name];
      if (!a) return;
      a.setEffectiveWeight(THREE.MathUtils.lerp(a.getEffectiveWeight(), w, fade));
      a.setEffectiveTimeScale(name === 'run' ? 1.15 : name === 'walk' ? 1.05 : 1);
    };
    setW('idle', target === 'idle' ? 1 : 0);
    setW('walk', target === 'walk' ? 1 : 0);
    setW('run', target === 'run' ? 1 : 0);

    // Optional light en garde only while fully idle (and only if clip exists)
    const eg = this.combat.Combat_EnGarde;
    if (eg) {
      // Keep off on Mixamo retarget — Idle already poses arms; layering twists them
      const want = 0;
      eg.setEffectiveWeight(THREE.MathUtils.lerp(eg.getEffectiveWeight(), want, fade));
    }
  }

  /**
   * Fallback body language only for models WITHOUT embedded combat clips.
   * Mixamo-retargeted fencer never uses this (wrong axis assumptions).
   */
  private applyCombatPose(): void {
    if (!this.skinned || this.hasEmbeddedCombat) return;
    const s = this.state;
    const t = s.actionDur > 0 ? Math.min(1, s.actionT / s.actionDur) : 0;
    const ease = Math.sin(t * Math.PI);
    const arm = this.bones.rightArm;
    const fore = this.bones.rightForeArm;
    const spine = this.bones.spine1 ?? this.bones.spine;

    const clipWeight = this.activeCombat?.getEffectiveWeight() ?? 0;
    if (clipWeight > 0.55) return;
    if (s.action === 'idle' || s.action === 'advance' || s.action === 'retreat') return;

    if (s.action === 'lunge' || s.action === 'riposte') {
      if (arm) addLocalEuler(arm, THREE.MathUtils.lerp(0, -0.5, ease), 0, 0);
      if (fore) addLocalEuler(fore, THREE.MathUtils.lerp(0, 0.25, ease), 0, 0);
      if (spine) addLocalEuler(spine, 0.15 * ease, 0, 0);
    } else if (s.action === 'fleche') {
      if (arm) addLocalEuler(arm, -0.6, 0, 0);
      if (spine) addLocalEuler(spine, 0.2, 0, 0);
    } else if (s.action === 'parryHigh') {
      if (arm) addLocalEuler(arm, 0.3, 0.35, 0.15);
      if (fore) addLocalEuler(fore, -0.4, 0, 0);
    } else if (s.action === 'parryLow') {
      if (arm) addLocalEuler(arm, -0.35, -0.3, 0.1);
    } else if (s.action === 'recover') {
      if (arm) addLocalEuler(arm, -0.2 * (1 - t), 0, 0);
    }
  }

  /**
   * Mouse aim AFTER mixer.
   * During combat: socket only (no bone twists — snapshot showed shoulder at ~140°).
   * Idle/move: mild arm + forearm, no hand/shoulder stack.
   */
  private applyMouseAimToArm(): void {
    const s = this.state;
    const yaw = s.bladeAngle;
    const pitch = s.stance;
    const clipW = this.activeCombat?.getEffectiveWeight() ?? 0;
    const combat = clipW > 0.15;

    // Socket always tracks aim (sword tip), independent of skeletal twist
    const sockScale = combat ? 0.55 : 1;
    this.weaponSocket.rotation.x = Math.PI * 0.5 + pitch * 0.4 * sockScale;
    this.weaponSocket.rotation.z = Math.PI * 0.5 + yaw * 0.45 * sockScale;
    this.weaponSocket.rotation.y = yaw * 0.12 * sockScale;

    if (combat) return; // do not fight Mixamo combat bone rotations

    const arm = this.bones.rightArm;
    const fore = this.bones.rightForeArm;
    // Mild aim — Mixamo local axes: prefer small X/Y, avoid stacked shoulder+hand
    if (arm) addLocalEuler(arm, pitch * 0.45, yaw * 0.55, yaw * 0.08);
    if (fore) addLocalEuler(fore, pitch * 0.2, yaw * 0.08, 0);
  }

  /** Snapshot for the anim debug console (P1). */
  captureDebugSnapshot(): AnimDebugSnapshot {
    const boneOf = (o?: THREE.Object3D) => {
      if (!o) return { ex: 0, ey: 0, ez: 0, qx: 0, qy: 0, qz: 0, qw: 1 };
      _eul.setFromQuaternion(o.quaternion, o.rotation.order);
      return {
        ex: _eul.x,
        ey: _eul.y,
        ez: _eul.z,
        qx: o.quaternion.x,
        qy: o.quaternion.y,
        qz: o.quaternion.z,
        qw: o.quaternion.w,
      };
    };
    const loco: Record<string, number> = {};
    for (const k of ['idle', 'walk', 'run'] as const) {
      const a = this.loco[k];
      if (a) loco[k] = a.getEffectiveWeight();
    }
    const combat: Record<string, number> = {};
    for (const [k, a] of Object.entries(this.combat)) {
      if (a) combat[k] = a.getEffectiveWeight();
    }
    const notes: string[] = [];
    if (this.hasEmbeddedCombat) notes.push('embeddedCombat');
    notes.push(`url=${this.loadedUrl}`);
    // Detect non-finite / wild quats
    for (const [label, b] of [
      ['rArm', this.bones.rightArm],
      ['rHand', this.bones.rightHand],
    ] as const) {
      if (!b) continue;
      const n = b.quaternion.length();
      if (!Number.isFinite(n) || Math.abs(n - 1) > 0.15) {
        notes.push(`${label} quatLen=${n.toFixed(3)}`);
        animDebug.warn(`${label} quaternion length ${n.toFixed(3)}`);
      }
    }
    return {
      t: performance.now() / 1000,
      action: this.state.action,
      actionT: this.state.actionT,
      blade: this.state.bladeAngle,
      stance: this.state.stance,
      loco,
      combat,
      bones: {
        rShoulder: boneOf(this.bones.rightShoulder),
        rArm: boneOf(this.bones.rightArm),
        rFore: boneOf(this.bones.rightForeArm),
        rHand: boneOf(this.bones.rightHand),
        lArm: boneOf(this.bones.leftArm),
        hips: boneOf(this.bones.hips),
      },
      notes,
    };
  }

  aiDrive(dt: number, desiredX: number, action: BladeAction | null): void {
    const s = this.state;
    if (action && (s.action === 'idle' || s.action === 'advance' || s.action === 'retreat')) {
      if (action === 'lunge') this.startAction('lunge', 0.52);
      else if (action === 'fleche') this.startAction('fleche', 0.48);
      else if (action === 'parryHigh') this.startAction('parryHigh', 0.36);
      else if (action === 'parryLow') this.startAction('parryLow', 0.36);
      else if (action === 'riposte' && s.canRiposte) this.startAction('riposte', 0.36);
      else if (action === 'advance' || action === 'retreat') s.action = action;
    }
    let move = 0;
    if (s.action === 'idle' || s.action === 'advance' || s.action === 'retreat') {
      const dx = desiredX - s.x;
      if (Math.abs(dx) > 0.08) {
        move = Math.sign(dx);
        s.action = move * s.facing > 0 ? 'advance' : 'retreat';
      } else {
        s.action = 'idle';
      }
    }
    this.update(dt, move, false, false, 0, false);
  }

  private syncTransform(): void {
    this.root.position.set(this.state.x, 0, 0);
    this.root.rotation.y = this.state.facing > 0 ? Math.PI / 2 : -Math.PI / 2;
  }

  private updateTip(): void {
    if (this.bladeTip) this.bladeTip.getWorldPosition(this.tipWorld);
    this.lameProxy.getWorldPosition(this.lameCenter);
    this.chestBox.min.set(
      this.lameCenter.x - 0.22,
      this.lameCenter.y - 0.28,
      this.lameCenter.z - 0.18,
    );
    this.chestBox.max.set(
      this.lameCenter.x + 0.22,
      this.lameCenter.y + 0.28,
      this.lameCenter.z + 0.18,
    );
  }

  getEyeWorld(out: THREE.Vector3): THREE.Vector3 {
    if (this.bones.head) {
      this.bones.head.getWorldPosition(out);
      out.y += 0.06;
      return out;
    }
    out.set(this.state.x, 1.65, 0);
    return out;
  }

  getChestWorld(out: THREE.Vector3): THREE.Vector3 {
    return this.lameProxy.getWorldPosition(out);
  }
}

export const PISTE_LENGTH = PISTE_HALF * 2;
